<script lang="ts">
  import type { DecodedImage } from '$lib/image/decode'
  import type { CropRect } from '$lib/types'

  interface Props {
    image: DecodedImage
    svg: string
    /** The region that was converted, so both panes show the same thing. */
    crop?: CropRect | null
  }

  const { image, svg, crop = null }: Props = $props()

  /** Percentage of the width showing the vectorized result. */
  let split = $state(50)
  let zoom = $state(1)
  let panX = $state(0)
  let panY = $state(0)
  let dragging = $state(false)

  let canvas = $state<HTMLCanvasElement | null>(null)
  let stage = $state<HTMLDivElement | null>(null)
  let dragOrigin = { x: 0, y: 0, panX: 0, panY: 0 }

  $effect(() => {
    const target = canvas
    if (!target) return
    const region = crop ?? { x: 0, y: 0, width: image.width, height: image.height }
    target.width = region.width
    target.height = region.height
    const ctx = target.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, region.width, region.height)
    // Only the converted region: the vectorized pane covers exactly that, and
    // showing the whole image beside it would compare two different framings.
    ctx.drawImage(
      image.bitmap,
      region.x,
      region.y,
      region.width,
      region.height,
      0,
      0,
      region.width,
      region.height,
    )
  })

  const MIN_ZOOM = 0.5
  const MAX_ZOOM = 16

  // One derived string, applied to both panes: they are separate elements —
  // the vectorized one has to sit inside a screen-space clip — but there is
  // still only one transform to get right, so they cannot drift apart.
  const transform = $derived(`translate(${panX}px, ${panY}px) scale(${zoom})`)

  function onWheel(event: WheelEvent) {
    if (!stage) return
    event.preventDefault()
    const rect = stage.getBoundingClientRect()
    const cursorX = event.clientX - rect.left
    const cursorY = event.clientY - rect.top
    const next = clamp(zoom * Math.exp(-event.deltaY * 0.0015), MIN_ZOOM, MAX_ZOOM)
    // Keep the point under the cursor fixed while scaling.
    const factor = next / zoom
    panX = cursorX - (cursorX - panX) * factor
    panY = cursorY - (cursorY - panY) * factor
    zoom = next
  }

  function onPointerDown(event: PointerEvent) {
    if (event.button !== 0) return
    dragging = true
    dragOrigin = { x: event.clientX, y: event.clientY, panX, panY }
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  }

  function onPointerMove(event: PointerEvent) {
    if (!dragging) return
    panX = dragOrigin.panX + (event.clientX - dragOrigin.x)
    panY = dragOrigin.panY + (event.clientY - dragOrigin.y)
  }

  function onPointerUp(event: PointerEvent) {
    dragging = false
    const element = event.currentTarget as HTMLElement
    if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId)
  }

  export function reset() {
    zoom = 1
    panX = 0
    panY = 0
  }

  function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
  }
</script>

<div class="compare" data-testid="compare-view">
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="stage"
    class:dragging
    bind:this={stage}
    onwheel={onWheel}
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={onPointerUp}
    onpointercancel={onPointerUp}
    data-testid="compare-stage"
  >
    <div class="pane" style:transform data-testid="compare-pane-original">
      <canvas bind:this={canvas} data-testid="compare-original"></canvas>
    </div>

    <!-- The reveal is clipped in *screen* space, outside the transform. Clipping
         inside it would tie the split to the artwork, so panning would drag the
         boundary along with the image and leave the far side never compared. -->
    <div class="reveal" style:clip-path="inset(0 0 0 {split}%)">
      <div class="pane" style:transform data-testid="compare-pane-vector">
        <!-- Generated by the exporter, which escapes every interpolated value. -->
        <div class="vector" data-testid="compare-vector">{@html svg}</div>
      </div>
    </div>

    <div class="seam" style:left="{split}%"></div>
  </div>

  <div class="controls">
    <label for="split">Original</label>
    <input
      id="split"
      type="range"
      min="0"
      max="100"
      bind:value={split}
      data-testid="compare-slider"
      aria-label="Reveal the vectorized result"
    />
    <label for="split">Vectorized</label>

    <span class="zoom" data-testid="compare-zoom">{Math.round(zoom * 100)}%</span>
    <button type="button" onclick={reset} data-testid="compare-reset">Reset view</button>
  </div>
</div>

<style>
  .compare {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .stage {
    position: relative;
    overflow: hidden;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    cursor: grab;
    touch-action: none;
    aspect-ratio: 1;
    max-height: 70vh;
  }

  .stage.dragging {
    cursor: grabbing;
  }

  .pane {
    position: absolute;
    inset: 0;
    transform-origin: 0 0;
  }

  .reveal {
    position: absolute;
    inset: 0;
  }

  canvas,
  .vector {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .vector :global(svg) {
    width: 100%;
    height: 100%;
  }

  .seam {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--accent);
    opacity: 0.7;
    pointer-events: none;
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.85rem;
    color: var(--text-muted);
  }

  input[type='range'] {
    flex: 1;
    min-width: 6rem;
  }

  .zoom {
    font-family: var(--mono);
    min-width: 3.5rem;
    text-align: right;
  }

  button {
    padding: 0.25rem 0.6rem;
    background: var(--surface-raised);
    border: 1px solid var(--border-strong);
    border-radius: 6px;
    font-size: 0.85rem;
  }
</style>
